import React, { useState, useRef, useCallback, useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@components/shadcn-ui/button";
import { Card } from "@components/shadcn-ui/card";
import { DynamicFormField, FieldType } from "@components/ui/DynamicInput";
import { useToast } from "@customHooks/useToast";
import { Loader2, Camera, X, ChevronLeft } from "lucide-react";
import axiosInstance from "config/axiosConfig";
import { getPresignedUrl, uploadFileToPresignedUrl } from "../../features/createApplication/service/uploadFileService";
import { applicationService } from "../../features/createApplication/service/applicationService";
import { useGeolocation } from "@customHooks/useGeolocation";
import { INDIAN_STATES,CITY_MAP } from "features/loanApplications/formFieldsConfig/indiaStatesList";
import {isFieldAsCity, encodeURIComponent } from "@lib/utils";



interface ResidenceVerificationData {
  addressIsSameAsKYC: string;
  occupancyStatus: string;
  applicantStayStatus: string;
  noOfOccupant: string;
  noOfEarningMember: string;
  noOfMembersDependentOnApplicant: string;
  noOfApplicantChildrenStudying: string;
  houseWithKitchen: string;
  electricityBillNo: string;
  electricityBillAmount: string;
  electricityBillOverdueAmount: string;
  monthlyResidentialExpenses: string;
  houseAccessibility: string;
  noOfRooms: string;
  gatedHouse: string;
  communityDominatedArea: string;
  spouseName: string;
  spouseAwareOfLoan: string;
  appliedLoanAmount: string;
  thirdPartyReference: string;
  standardOfLiving: string;
  agentRemarks: string;
  addressType: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  country: string;
  postalCode: string;
  ownership: string;
  monthlyRent: string;
  yearsOfStay: string;
}

const FORM_SECTIONS = {
  address: [
    { name: "addressIsSameAsKYC", label: "Address is same as KYC", type: "select" as const, options: ["Yes", "No"], validationRules: { required: "This field is required" } },
    { name: "addressType", label: "Address Type", type: "select" as const, options: ["PERMANENT", "TEMPORARY"], validationRules: { required: "This field is required" } },
    { name: "addressLine1", label: "Address Line 1", type: "text" as const, validationRules: { required: "This field is required" } },
    { name: "addressLine2", label: "Address Line 2", type: "text" as const, validationRules: {} },
    { name: "city", label: "City", type: "select" as const, options: [], validationRules: { required: "This field is required" } },
    { name: "state", label: "State", type: "select" as const, options: INDIAN_STATES, validationRules: { required: "This field is required" } },
    { name: "country", label: "Country", type: "text" as const, validationRules: { required: "This field is required" } },
    { name: "postalCode", label: "Postal Code", type: "text" as const, validationRules: { required: "This field is required", pattern: { value: /^\d{5,6}$/, message: "Enter valid postal code" } } }
  ],
  occupancy: [
    { name: "ownership", label: "Ownership", type: "select" as const, options: ["OWNED", "RENTED"], validationRules: { required: "This field is required" } },
    { name: "occupancyStatus", label: "Occupancy Status", type: "select" as const, options: ["Owned", "Rented", "Bachelor", "Parental", "Joint Family"], validationRules: { required: "This field is required" } },
    { name: "applicantStayStatus", label: "Applicant Stay Status", type: "select" as const, options: ["Permanent", "Temporary", "Does not stay"], validationRules: { required: "This field is required" } },
    { name: "monthlyRent", label: "Monthly Rent", type: "text" as const, validationRules: { required: "This field is required", pattern: { value: /^\d+(\.\d{1,2})?$/, message: "Enter valid amount" } } },
    { name: "yearsOfStay", label: "Years of Stay", type: "text" as const, validationRules: { required: "This field is required", pattern: { value: /^\d+$/, message: "Enter valid number" } } }
  ],
  household: [
    { name: "noOfOccupant", label: "No of Occupant", type: "select" as const, options: Array.from({ length: 10 }, (_, i) => (i + 1).toString()), validationRules: { required: "This field is required" } },
    { name: "noOfEarningMember", label: "No of Earning Member", type: "select" as const, options: Array.from({ length: 11 }, (_, i) => i.toString()), validationRules: { required: "This field is required" } },
    { name: "noOfMembersDependentOnApplicant", label: "No of Members Dependent on Applicant", type: "select" as const, options: Array.from({ length: 6 }, (_, i) => i.toString()), validationRules: { required: "This field is required" } },
    { name: "noOfApplicantChildrenStudying", label: "No of Applicant's Children Studying", type: "select" as const, options: Array.from({ length: 5 }, (_, i) => i.toString()), validationRules: { required: "This field is required" } }
  ],
  property: [
    { name: "noOfRooms", label: "No of Rooms", type: "select" as const, options: Array.from({ length: 10 }, (_, i) => (i + 1).toString()), validationRules: { required: "This field is required" } },
    { name: "houseWithKitchen", label: "House with Kitchen", type: "select" as const, options: ["Yes", "No"], validationRules: { required: "This field is required" } },
    { name: "houseAccessibility", label: "House Accessibility", type: "select" as const, options: ["No road", "2W road", "4W road"], validationRules: { required: "This field is required" } },
    { name: "gatedHouse", label: "Gated House", type: "select" as const, options: ["Yes", "No"], validationRules: { required: "This field is required" } },
    { name: "communityDominatedArea", label: "Community Dominated Area", type: "select" as const, options: ["Yes", "No"], validationRules: { required: "This field is required" } }
  ],
  financial: [
    { name: "electricityBillNo", label: "Electricity Bill No", type: "text" as const, validationRules: { required: "This field is required" } },
    { name: "electricityBillAmount", label: "Electricity Bill Amount", type: "text" as const, validationRules: { required: "This field is required", pattern: { value: /^\d+(\.\d{1,2})?$/, message: "Enter valid amount" } } },
    { name: "electricityBillOverdueAmount", label: "Electricity Bill Overdue Amount", type: "text" as const, validationRules: { required: "This field is required", pattern: { value: /^\d+(\.\d{1,2})?$/, message: "Enter valid amount" } } },
    { name: "monthlyResidentialExpenses", label: "Monthly Residential Expenses", type: "text" as const, validationRules: { required: "This field is required", pattern: { value: /^\d+(\.\d{1,2})?$/, message: "Enter valid amount" } } },
    { name: "appliedLoanAmount", label: "Applied Loan Amount", type: "text" as const, validationRules: { required: "This field is required", pattern: { value: /^\d+(\.\d{1,2})?$/, message: "Enter valid amount" } } }
  ],
  spouse: [
    { name: "spouseName", label: "Spouse Name", type: "text" as const, validationRules: {} },
    { name: "spouseAwareOfLoan", label: "Spouse Aware of Loan", type: "select" as const, options: ["Yes", "No"], validationRules: { required: "This field is required" } }
  ],
  verification: [
    { name: "thirdPartyReference", label: "Third Party Reference", type: "select" as const, options: ["Negative", "Positive"], validationRules: { required: "This field is required" } },
    { name: "standardOfLiving", label: "Standard of Living", type: "select" as const, options: ["High", "Medium", "Low", "Poor"], validationRules: { required: "This field is required" } },
    { name: "agentRemarks", label: "Agent Remarks", type: "textarea" as const, validationRules: {} }
  ]
};

export const ResidenceVerificationPage: React.FC = () => {
  const { applicationId } = useParams<{ applicationId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [capturedPhotos, setCapturedPhotos] = useState<string[]>([]);
  const [applicationData, setApplicationData] = useState<any>(null);
  
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  
  const { userLocation, locationError, isLocationAllowed } = useGeolocation();

  const form = useForm<ResidenceVerificationData>({
    mode: "all",
    defaultValues: {
      addressType: "PERMANENT",
      country: "India",
      ownership: "OWNED",
      monthlyRent: "0",
      yearsOfStay: "1"
    }
  });

  React.useEffect(() => {
    const fetchApplicationData = async () => {
      if (applicationId) {
        try {
          const data = await applicationService.getApplicationById(applicationId);
          setApplicationData(data);
        } catch (error) {
          console.error("Error fetching application data:", error);
        }
      }
    };
    fetchApplicationData();
  }, [applicationId]);
  
  useEffect(() => {
  form.setValue("city", "");
}, [form.watch("state")]);


  const startCamera = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          facingMode: 'environment',
          width: { ideal: 1280 },
          height: { ideal: 720 }
        } 
      });
      
      setShowCamera(true);
      
      // Wait for the modal to render before setting up video
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          streamRef.current = stream;
          
          // Ensure video plays when metadata is loaded
          videoRef.current.onloadedmetadata = () => {
            videoRef.current?.play();
          };
        }
      }, 100);
    } catch (error) {
      toast({
        variant: "destructive",
        description: "Failed to access camera. Please check permissions."
      });
    }
  }, [toast]);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setShowCamera(false);
  }, []);

  const capturePhoto = useCallback(() => {
    if (videoRef.current && canvasRef.current) {
      const canvas = canvasRef.current;
      const video = videoRef.current;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(video, 0, 0);
        const photoDataUrl = canvas.toDataURL('image/jpeg', 0.8);
        setCapturedPhotos(prev => [...prev, photoDataUrl]);
        stopCamera();
      }
    }
  }, [stopCamera, capturedPhotos.length]);

  const removePhoto = useCallback((index: number) => {
    setCapturedPhotos(prev => prev.filter((_, i) => i !== index));
  }, []);

  const uploadPhotos = async (photos: string[]): Promise<string[]> => {
    if (!applicationId)
      throw new Error("Application Id cannot be null or undefined!");

    const fileNames = [];
    
    for (let i = 0; i < photos.length; i++) {
      const photo = photos[i];
      const response = await fetch(photo);
      const blob = await response.blob();
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const fileName = `residence-photo-${i + 1}-${timestamp}.jpg`;
      const file = new File([blob], fileName, { type: 'image/jpeg' });

      const presignedResponse = await getPresignedUrl({
        filename: file.name,
        documentType: "RESIDENCE_VERIFICATION_FORM",
        applicationId
      });
      
      await uploadFileToPresignedUrl(file, presignedResponse.fileUploadUrl, () => {});

      fileNames.push(fileName)
    }

    return fileNames;
  };

  const onSubmit = async (data: ResidenceVerificationData) => {
    if (!applicationId) {
      toast({
        variant: "destructive",
        description: "Application ID is required"
      });
      return;
    }

    if (!data.addressLine1 || !data.city || !data.state) {
      toast({
        variant: "destructive",
        description: "Please add address details"
      });
      return;
    }

    if (capturedPhotos.length < 5) {
      toast({
        variant: "destructive",
        description: "Please capture at least five photos"
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const fileNames = await uploadPhotos(capturedPhotos);

      const verificationPayload = {
        resourceId: applicationId,
        address: {
          type: data.addressType,
          line1: data.addressLine1,
          line2: data.addressLine2,
          city: data.city,
          state: data.state,
          country: data.country,
          latitude: userLocation?.latitude || 0,
          longitude: userLocation?.longitude || 0,
          postalCode: parseInt(data.postalCode),
          ownership: data.ownership,
          monthlyRent: parseFloat(data.monthlyRent),
          yearsOfStay: parseInt(data.yearsOfStay),
          active: true,
          verified: false
        },
        additionalResidentialDetails: {
          spouseName: data.spouseName || '',
          spouseAwareOfLoan: data.spouseAwareOfLoan == 'Yes',
          livingStandard: data.standardOfLiving,
          accessibility: data.houseAccessibility,
          electricityBillNo: data.electricityBillNo,
          electricityBillAmount: parseFloat(data.electricityBillAmount),
          overdueElectricityBillAmount: parseFloat(data.electricityBillOverdueAmount),
          gatedHouse: data.gatedHouse === 'Yes',
          kitchen: data.houseWithKitchen === 'Yes',
          communityDominatedArea: data.communityDominatedArea === 'Yes',
          earningMemberCount: parseInt(data.noOfEarningMember),
          occupantsCount: parseInt(data.noOfOccupant),
          childrenStudyingCount: parseInt(data.noOfApplicantChildrenStudying),
          roomsCount: parseInt(data.noOfRooms)
        },
        stayStatus: data.occupancyStatus.toUpperCase(),
        thirdPartyReference: data.thirdPartyReference === 'Positive',
        remarks: data.agentRemarks,
        documents: fileNames
      };

      await axiosInstance.post('/verification', verificationPayload);
      
      // Call action API with VERIFICATION_COMPLETE action type
      await applicationService.performAction(
          applicationData,
          "VERIFICATION_COMPLETE",
          applicationData.leadId
      );
      
      toast({
        description: "Residence verification submitted successfully"
      });
      
      navigate(`/dashboard/residence-verification/view/${applicationId}?submitted=true`);
    } catch (error) {
      toast({
        variant: "destructive",
        description: "Failed to submit residence verification"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (locationError || !isLocationAllowed) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Card className="p-6 max-w-md">
          <div className="text-center space-y-4">
            <h2 className="text-xl font-bold text-red-600">Location Access Required</h2>
            <p className="text-gray-600">{locationError || "Location permission is required to proceed with residence verification."}</p>
            <Button onClick={() => window.location.reload()} className="w-full">
              Retry
            </Button>
          </div>
        </Card>
      </div>
    );
  }

 
  return (
    <div>
      <Card className="p-6">
        <div className="mb-6">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(`/dashboard/application/details/${encodeURIComponent(applicationId!)}`)}
            className="flex items-center gap-2 mb-4"
          >
            <ChevronLeft className="h-4 w-4" />
            <span className="sm:inline">Application</span>
          </Button>
          <h2 className="text-2xl font-bold">Residence Verification</h2>
        </div>
        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <div className="space-y-4">
              <h3 className="text-lg font-semibold border-b pb-2">Address Details</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {FORM_SECTIONS.address.map((field, index) => { 
                  
                  const selectedState = form.watch("state"); 
                  const dynamicOptions = isFieldAsCity(field.name) ? CITY_MAP[selectedState] || []  : field.options || []; 

                  return(
                    <div key={index} className="w-full">
                      <DynamicFormField field={{...field, options : dynamicOptions} as FieldType} isDisabled={isSubmitting || (isFieldAsCity(field.name) && !selectedState)} />
                    </div>
                    
                  );  
              })}
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-semibold border-b pb-2">Occupancy Details</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {FORM_SECTIONS.occupancy.map((field) => (
                  <DynamicFormField field={field} isDisabled={isSubmitting} />
                ))}
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-semibold border-b pb-2">Household Information</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {FORM_SECTIONS.household.map((field) => (
                  <DynamicFormField field={field} isDisabled={isSubmitting} />
                ))}
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-semibold border-b pb-2">Property Details</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {FORM_SECTIONS.property.map((field) => (
                  <DynamicFormField field={field} isDisabled={isSubmitting} />
                ))}
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-semibold border-b pb-2">Financial Information</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {FORM_SECTIONS.financial.map((field) => (
                  <DynamicFormField field={field} isDisabled={isSubmitting} />
                ))}
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-semibold border-b pb-2">Spouse Details</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {FORM_SECTIONS.spouse.map((field) => (
                  <DynamicFormField field={field} isDisabled={isSubmitting} />
                ))}
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-semibold border-b pb-2">Verification Details</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {FORM_SECTIONS.verification.map((field) => (
                  <DynamicFormField field={field} isDisabled={isSubmitting} />
                ))}
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-semibold border-b pb-2">Capture Photos ({capturedPhotos.length})</h3>
              
              {(
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {capturedPhotos.map((photo, index) => (
                    <div key={index} className="relative">
                      <img src={photo} alt={`Photo ${index + 1}`} className="w-full h-48 object-cover rounded border" />
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        className="absolute top-2 right-2"
                        onClick={() => removePhoto(index)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              
              {(
                <Button
                  type="button"
                  variant="outline"
                  onClick={startCamera}
                  className="flex items-center gap-2"
                >
                  <Camera className="h-4 w-4" />
                  Capture Photo ({capturedPhotos.length})
                </Button>
              )}
            </div>

            <Button
              type="submit"
              disabled={isSubmitting || !form.formState.isValid || capturedPhotos.length < 5 || !userLocation}
              className="w-full"
            >
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Submit Residence Verification
            </Button>
          </form>
        </FormProvider>
      </Card>

      {showCamera && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-4 sm:p-6 rounded-lg w-full mx-2 sm:mx-4 max-w-2xl">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">Capture Photo</h2>
              <Button variant="ghost" size="sm" onClick={stopCamera}>
                <X className="h-4 w-4" />
              </Button>
            </div>
            <div className="space-y-4">
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="w-full h-64 bg-black rounded"
                style={{ objectFit: 'cover' }}
              />
              <div className="flex gap-2 justify-center">
                <Button onClick={capturePhoto} disabled={false}>
                  <Camera className="mr-2 h-4 w-4" />
                  Capture
                </Button>
                <Button variant="outline" onClick={stopCamera}>
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <canvas ref={canvasRef} style={{ display: 'none' }} />
    </div>
  );
};